import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import * as buttons from 'smg/buttons';
import { GetQuotes } from 'smg/customer-api/v2/quotes';
import { GetLineItems } from 'smg/customer-api/v2/lineitems';
import * as models from 'smg/customer-api/models';
import { OrderAggregates, Orders } from 'smg/customer-api/orders';
import * as session from 'smg/customer-api/session';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { AccountReady } from '../account-session';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as payments from '../payments';

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const loader = document.querySelector('#dashboard-loader') as HTMLElement;
    const accountBalance = document.querySelector('#account-balance') as HTMLElement;
    const accountBalanceAmount = accountBalance.querySelector('#account-balance-amount') as HTMLElement;
    const availableDiscount = document.querySelector('#available-discount') as HTMLElement;
    const availableDiscountAmount = availableDiscount.querySelector('#available-discount-amount') as HTMLElement;
    const availableDiscountDetails = availableDiscount.querySelector('#available-discount-details') as HTMLElement;

    const firstTimer = document.querySelector('#first-timer') as HTMLElement;

    const latestQuoteContainer = document.querySelector('#latest-quote-container') as HTMLElement;
    const quoteTotal = document.querySelector('#quote-total') as HTMLElement;
    const quoteSummary = document.querySelector('#quote-summary') as HTMLElement;
    const addToCart = document.querySelector('#add-to-cart-btn') as HTMLButtonElement;
    const prevBtn = document.querySelector('#prev-btn') as HTMLButtonElement;
    const nextBtn = document.querySelector('#next-btn') as HTMLButtonElement;
    const proofEls: Element[] = [];

    const orderHistory = document.querySelector('#order-history') as HTMLElement;
    const orderHistoryCount = orderHistory.querySelector('#order-history-count') as HTMLElement;
    const orderHistoryTotal = orderHistory.querySelector('#order-history-total') as HTMLElement;

    const unpaidEl = document.querySelector('#unpaid') as HTMLElement;
    const unpaidCountEl = unpaidEl.querySelector('#unpaid-count') as HTMLElement;
    const unpaidTotalEl = unpaidEl.querySelector('#unpaid-total') as HTMLElement;
    const unpaidPayEl = unpaidEl.querySelector('#unpaid-pay') as HTMLElement;

    const showCards: HTMLElement[] = [];

    // The cached session can hold a balance or discount already spent in
    // checkout or in the IAP, and this page shows both as money.
    session.ClearCache();
    const sess = session.Session();
    const discount = session.SessionWith(['availableDiscount']);

    void Promise.all([sess, discount]).then(([sess, discount]) => {
        if (sess.user.balance?.gt('0')) {
            accountBalanceAmount.textContent = DollarFormat(sess.user.balance);
            showCards.push(accountBalance);
        }
        // availableDiscount is a data= field on the users read.
        if (discount.user.availableDiscount.gt('0')) {
            availableDiscountAmount.textContent = DollarFormat(discount.user.availableDiscount);
            bootstrap.Collapse.getOrCreateInstance(availableDiscountDetails, { toggle: false });
            showCards.push(availableDiscount);
        }
    });

    const lineItems = GetLineItems({ data: 'name,pluralName,product' });

    const latestQuoteP = GetQuotes({
        sort: 'created desc',
        max: 1,
        data: 'id,key,cachedTotal,quoteLineItems,quoteProofs',
    }).then(async data => {
        const latestQuote = data.results[0];

        if (!latestQuote) {
            return;
        }

        const viewQuoteBtn = document.querySelector('#view-quote-btn') as HTMLAnchorElement;
        viewQuoteBtn.href = `${Relref('/quote')}?id=${latestQuote.key}`;

        addToCart.addEventListener('click', () => {
            buttons.Start(addToCart);
            void carts.AddCartItem({ quoteID: latestQuote.id || '' }).then(() => {
                addToCart.classList.remove('btn-primary', 'btn-success', 'text-white');
                addToCart.classList.add('btn-outline-success');

                addToCart.querySelector('i')?.classList.remove('fa-shopping-cart');
                addToCart.querySelector('i')?.classList.add('fa-check');
            }).finally(() => buttons.Stop(addToCart));
        });

        showCards.push(latestQuoteContainer);

        if (latestQuote.quoteLineItems.length) {
            const qtys: Record<string, number> = {};
            for (const quoteLineItem of latestQuote.quoteLineItems) {
                if (!quoteLineItem.quantity) {
                    continue;
                }

                const lineItem = (await lineItems).get(quoteLineItem.lineItemID || '');

                if (!(quoteLineItem.lineItem || lineItem?.product)) {
                    continue;
                }

                const name = quoteLineItem.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                qtys[name] = (qtys[name] || 0) + quoteLineItem.quantity;
            }
            quoteSummary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
        }

        if (latestQuote.quoteProofs.length) {
            for (const op of latestQuote.quoteProofs) {
                const p = op.proof;
                if (!p) {
                    continue;
                }
                if (latestQuote.quoteProofs.length > 1) {
                    prevBtn.classList.remove('d-none');
                    nextBtn.classList.remove('d-none');
                }

                const name = op.number;

                const thumbnailContainer = document.querySelector('#thumbnail-container') as HTMLElement;
                const thumbEl = template.Clone('#thumbnail-template').firstElementChild as HTMLElement;
                proofEls.push(thumbEl);
                const quoteThumbnail = thumbEl.querySelector('.quote-proof-thumbnail') as HTMLImageElement;
                const thumbAnchor = thumbEl.querySelector('.thumb-anchor') as HTMLAnchorElement;

                const urls = new models.Proof(p).getURLs();

                thumbAnchor.href = urls.main;
                thumbAnchor.dataset.caption = `Proof ${name || ''}`;
                thumbAnchor.setAttribute('data-fancybox', latestQuote.id || '');

                quoteThumbnail.src = urls.thumbnail;
                quoteThumbnail.alt = `Proof ${name || ''}`;
                quoteThumbnail.title = `Proof ${name || ''}`;

                thumbnailContainer.appendChild(thumbEl);

                let currProof = 0;

                const showHideProofs = () => {
                    for (let i = 0; i < proofEls.length; i++) {
                        if (i === currProof) {
                            proofEls[i].classList.remove('d-none');
                        } else {
                            proofEls[i].classList.add('d-none');
                        }
                    }
                };
                nextBtn.addEventListener('click', () => {
                    currProof++;
                    if (currProof >= proofEls.length) {
                        currProof = 0;
                    }
                    showHideProofs();
                });
                prevBtn.addEventListener('click', () => {
                    currProof--;
                    if (currProof < 0) {
                        currProof = proofEls.length - 1;
                    }
                    showHideProofs();
                });
                showHideProofs();
            }
        }
        quoteTotal.textContent = DollarFormat(latestQuote.cachedTotal ?? 0);
    });

    const ordersStats = OrderAggregates('', ['id', 'total']).then(resp => {
        const count = resp.aggregates.get('id')?.count ?? 0;
        if (count) {
            orderHistoryCount.textContent = count.toLocaleString();
            showCards.push(orderHistory);
        }

        const sum = Big(resp.aggregates.get('total')?.sum ?? 0);
        if (sum.eq(0)) {
            return;
        }

        orderHistoryTotal.textContent = DollarFormat(sum);
        showCards.push(orderHistory);
    });

    const unpaidParams = {
        'q': 'balance:>=.01',
        'data': 'balance',
        'max': 9999,
    };
    const unpaidOrders = Orders(unpaidParams).then(data => {
        if (!data.results.length) {
            return;
        }

        const ids: string[] = [];
        let total = Big('0');

        const consumeResp = (orders: models.HyejooOrder[]) => {
            ids.length = 0;
            total = Big('0');
            for (const order of orders) {
                ids.push(order.id);
                total = total.add(order.balance);
            }

            unpaidCountEl.textContent = `${orders.length.toLocaleString()} ${orders.length === 1 ? 'Order' : 'Orders'}`;
            unpaidTotalEl.textContent = DollarFormat(total);
        };

        consumeResp(data.results);

        unpaidPayEl.addEventListener('click', () => payments.PromptPayment(undefined, ids, total, false).
            then(() => Orders(unpaidParams).then(data => {
                consumeResp(data.results);
                if (total.lt('.01')) {
                    unpaidEl.parentElement?.removeChild(unpaidEl);
                }
            })));

        showCards.push(unpaidEl);
    });

    await Promise.all([lineItems, latestQuoteP]);

    await sess;
    await ordersStats;
    await unpaidOrders;

    if (!showCards.length) {
        firstTimer.classList.remove('d-none');
    } else {
        showCards.forEach(el => el.classList.remove('d-none'));
    }

    loader.parentElement?.removeChild(loader);
}
